import { SlateEditor, SlateNode } from '@src/components/sco/RichMarkdownInput'
import { EditorElementTypes } from '@src/components/sco/RichMarkdownInput/components/Element'
import { MentionState } from '@src/components/sco/RichMarkdownInput/plugins/mentions/MentionSelector'
import { Editor, Range, Transforms } from 'slate'

export type Mentionable = {
  id?: string
  label?: string
  identifier?: string
  pictureUrl?: string
}

export const DEFAULT_MENTION_STATE: MentionState = {
  target: null,
  selectedIndex: 0,
  selectedMentionable: null,
  search: '',
}

export function withMentions(editor: SlateEditor, options: any = {}) {
  if (options?.disabled) {
    return editor
  }

  const { isInline, isVoid, markableVoid, onKeyDown, onChange } = editor

  editor.isInline = (element: SlateNode) => {
    return element.type == EditorElementTypes.MENTION ? true : isInline(element)
  }

  editor.isVoid = (element: SlateNode) => {
    return element.type == EditorElementTypes.MENTION ? true : isVoid(element)
  }

  editor.markableVoid = (element: SlateNode) => {
    return element.type == EditorElementTypes.MENTION ? true : markableVoid(element)
  }

  editor.insertMention = (editor, mentionable: Mentionable) => {
    const mentionNode = {
      type: 'mention',
      mentionable: mentionable || {},
      children: [{ text: '' }],
    }
    Transforms.insertNodes(editor, mentionNode)
    Transforms.move(editor)
  }

  editor.onKeyDown = (editor, event, options = {}) => {
    const mentionState = editor.mentionState

    if (mentionState?.target) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          editor.setMentionState(prevState => ({
            ...prevState,
            selectedIndex: prevState.selectedIndex + 1,
          }))
          return
          break
        case 'ArrowUp':
          event.preventDefault()
          editor.setMentionState(prevState => ({
            ...prevState,
            selectedIndex: prevState.selectedIndex - 1 || 0,
          }))
          return
          break
        case 'Tab':
        case 'Enter':
          if (mentionState.selectedMentionable) {
            Transforms.select(editor, mentionState.target)
            editor.insertMention(editor, mentionState.selectedMentionable)
          }
          event.preventDefault()
          editor.setMentionState(prevState => DEFAULT_MENTION_STATE)
          return
          break
        case 'Escape':
          event.preventDefault()
          editor.setMentionState(prevState => DEFAULT_MENTION_STATE)
          return
          break
      }
    }

    onKeyDown(editor, event)
  }

  editor.onChange = () => {
    if (editor.selection && Range.isCollapsed(editor.selection)) {
      const [start] = Range.edges(editor.selection)
      const end =
        start &&
        Editor.before(editor, start, {
          unit: 'offset',
          distance: start.offset,
        })
      const textRange = end && Editor.range(editor, end, start)
      const text = textRange && Editor.string(editor, textRange)
      const match = text?.match(/@([\w]+[\s][\w]+|[\w]+[\s]|[\w]+)$/)

      if (match) {
        const [targetText, matchText] = match
        const entity = Editor.before(editor, start, {
          unit: 'offset',
          distance: targetText.length,
        })
        const targetRange = entity && Editor.range(editor, entity, start)

        if (targetRange) {
          editor.setMentionState({
            target: targetRange,
            search: matchText,
            selectedIndex: 0,
          })
          return
        }
      } else {
        if (editor.mentionState.target) {
          editor.setMentionState(DEFAULT_MENTION_STATE)
        }
      }
    }

    if (onChange) {
      onChange()
    }
  }

  return editor
}
